import React, { useMemo, useState } from "react";
import { Col, Form, Input, InputNumber, Row, Select, Typography } from "antd";
import type { FormInstance } from "antd";
import debounce from "lodash/debounce";
import { fetchCatalogMedications } from "../../../../../api/healthcare/MedicationCatalogApi";
import { defaultDosageGuide, type MedicationCatalogItem } from "../../../../../types/healthcare/MedicationCatalogTypes";
import { PRESCRIPTION_PARTICULAR_KEYS, PRESCRIPTION_PARTICULAR_RULES, type PrescriptionParticularKey } from "../../../../../types/healthcare/PrescriptionDraftTypes";
import { can } from "../../../../../utils/userPrefs/validatePermissions";

const { Text } = Typography;

interface PrescriptionParticularsFieldsProps {
	form: FormInstance;
	disabled: boolean;
}

const WIDE_FIELDS: PrescriptionParticularKey[] = ["medicine_name", "directions_for_use", "reason_for_prescribe", "prescription_notes"];

// CU-14ygtkjeh2z: only fields the catalogue states unambiguously; dose, directions and quantity stay the prescriber's to write.
const catalogueSuggestion = (item: MedicationCatalogItem): Partial<Record<PrescriptionParticularKey, string>> => {
	const suggestion: Partial<Record<PrescriptionParticularKey, string>> = { medicine_name: item.name };
	const forms = item.dosage_forms ?? [];
	if (forms.length === 1) {
		if (forms[0].form) suggestion.medicine_form = forms[0].form;
		if (forms[0].concentration) suggestion.medicine_strength = forms[0].concentration;
	}
	const route = defaultDosageGuide(item.dosage_guides)?.administration_route;
	if (route) suggestion.route_of_administration = route;
	return suggestion;
};

const PrescriptionParticularsFields: React.FC<PrescriptionParticularsFieldsProps> = ({ form, disabled }) => {
	const [options, setOptions] = useState<{ value: number; label: string; item: MedicationCatalogItem }[]>([]);
	const [searching, setSearching] = useState(false);
	const [catalogueFailed, setCatalogueFailed] = useState(false);
	const catalogueAvailable = can("healthcare.catalogue.read");

	const search = useMemo(
		() =>
			debounce(async (term: string) => {
				if (term.trim().length < 2) {
					setOptions([]);
					return;
				}
				setSearching(true);
				try {
					const results = await fetchCatalogMedications({ search: term });
					setOptions(results.map((item) => ({ value: item.id, label: item.generic_name ? `${item.name} (${item.generic_name})` : item.name, item })));
					setCatalogueFailed(false);
				} catch {
					setOptions([]);
					setCatalogueFailed(true);
				} finally {
					setSearching(false);
				}
			}, 300),
		[]
	);

	const applyCatalogueItem = (id: number) => {
		const option = options.find((candidate) => candidate.value === id);
		if (!option) return;
		const suggestion = catalogueSuggestion(option.item);
		form.setFieldsValue({ particulars: suggestion });
		form.validateFields(Object.keys(suggestion).map((key) => ["particulars", key])).catch(() => undefined);
	};

	return (
		<>
			{catalogueAvailable && (
				<Form.Item
					label="Pre-fill from the clinic catalogue"
					extra={catalogueFailed ? "The catalogue could not be searched. Enter the medicine details directly." : "Fills the medicine name and, where the catalogue is unambiguous, form, strength and route. Review every value before saving."}
				>
					<Select
						showSearch
						allowClear
						filterOption={false}
						onSearch={search}
						onChange={(id?: number) => id !== undefined && applyCatalogueItem(id)}
						loading={searching}
						options={options}
						placeholder="Search the medication catalogue…"
						notFoundContent={searching ? "Searching…" : "Type at least two characters"}
						disabled={disabled}
						aria-label="Search the medication catalogue"
						data-testid="prescriptions-catalogue-search"
					/>
				</Form.Item>
			)}
			<Row gutter={[16, 0]}>
				{PRESCRIPTION_PARTICULAR_KEYS.map((key) => {
					const rule = PRESCRIPTION_PARTICULAR_RULES[key];
					return (
						<Col key={key} xs={24} md={WIDE_FIELDS.includes(key) ? 24 : 12}>
							<Form.Item
								label={rule.label}
								name={["particulars", key]}
								rules={
									rule.kind === "integer"
										? [{ type: "integer", min: rule.min, max: rule.max, message: `${rule.label} must be a whole number from ${rule.min} to ${rule.max}.` }]
										: [{ max: rule.max, message: `${rule.label} must be ${rule.max} characters or fewer.` }]
								}
							>
								{rule.kind === "integer" ? (
									<InputNumber min={rule.min} max={rule.max} precision={0} style={{ width: "100%" }} disabled={disabled} data-testid={`prescriptions-field-${key}`} />
								) : rule.multiline ? (
									<Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={rule.max} showCount disabled={disabled} data-testid={`prescriptions-field-${key}`} />
								) : (
									<Input maxLength={rule.max} disabled={disabled} data-testid={`prescriptions-field-${key}`} />
								)}
							</Form.Item>
						</Col>
					);
				})}
			</Row>
			<Text type="secondary" style={{ fontSize: 12 }}>
				A draft may be saved incomplete. Completeness and clinical checks happen at a later review step, not here.
			</Text>
		</>
	);
};

export default PrescriptionParticularsFields;
